/**
 * Background Sync Manager
 * Handles syncing of offline changes when connection is restored
 */

import { offlineStorage, OfflineData } from './offlineStorage';
import { supabase } from '@/integrations/supabase/client';

export interface SyncQueueItem {
  id: string;
  url: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body: any;
  headers: Record<string, string>;
  retryCount: number;
  maxRetries: number;
}

export type SyncStatus = 'idle' | 'syncing' | 'success' | 'error';

export interface SyncResult {
  success: boolean;
  syncedCount: number;
  failedCount: number;
  errors: Array<{ id: string; error: string }>;
}

class BackgroundSyncManager {
  private queue: SyncQueueItem[] = [];
  private isSyncing = false;
  private syncListeners: Array<(status: SyncStatus, result?: SyncResult) => void> = [];
  private maxRetries = 3;
  private baseDelay = 1000; // 1 second

  constructor() {
    // Listen for online events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        console.log('Connection restored, starting sync...');
        this.processQueue();
      });
    }
  }

  /**
   * Add a listener for sync status changes
   */
  onSyncStatusChange(callback: (status: SyncStatus, result?: SyncResult) => void): () => void {
    this.syncListeners.push(callback);
    return () => {
      this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners of status change
   */
  private notifyListeners(status: SyncStatus, result?: SyncResult): void {
    this.syncListeners.forEach(callback => callback(status, result));
  }

  /**
   * Add item to sync queue
   */
  async addToQueue(item: Omit<SyncQueueItem, 'id' | 'retryCount'>): Promise<void> {
    const queueItem: SyncQueueItem = {
      id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...item,
      retryCount: 0
    };

    this.queue.push(queueItem);
    console.log('Added to sync queue:', queueItem.id);

    // If online, try to process immediately
    if (navigator.onLine && !this.isSyncing) {
      await this.processQueue();
    }
  }

  /**
   * Process the sync queue
   */
  async processQueue(): Promise<SyncResult> {
    if (this.isSyncing) {
      console.log('Sync already in progress');
      return { success: false, syncedCount: 0, failedCount: 0, errors: [] };
    }

    if (!navigator.onLine) {
      console.log('Cannot sync while offline');
      return { success: false, syncedCount: 0, failedCount: 0, errors: [] };
    }

    this.isSyncing = true;
    this.notifyListeners('syncing');

    const result: SyncResult = {
      success: true,
      syncedCount: 0,
      failedCount: 0,
      errors: []
    };

    try {
      // Get all unsynced data from IndexedDB
      const unsyncedData = await offlineStorage.getUnsynced();
      console.log(`Processing ${unsyncedData.length} unsynced items`);

      // Process each item
      for (const data of unsyncedData) {
        try {
          const success = await this.syncOfflineData(data);
          
          if (success) {
            await offlineStorage.markAsSynced(data.id);
            result.syncedCount++;
          } else {
            await offlineStorage.incrementRetryCount(data.id);
            result.failedCount++;
            result.errors.push({
              id: data.id,
              error: 'Sync failed'
            });
          }
        } catch (error) {
          console.error('Error syncing item:', data.id, error);
          await offlineStorage.incrementRetryCount(data.id);
          result.failedCount++;
          result.errors.push({
            id: data.id,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      // Process in-memory queue items
      const queueCopy = [...this.queue];
      this.queue = [];

      for (const item of queueCopy) {
        try {
          const success = await this.executeRequest(item);
          
          if (success) {
            result.syncedCount++;
          } else if (item.retryCount < item.maxRetries) {
            // Re-add to queue with incremented retry count
            item.retryCount++;
            this.queue.push(item);
            result.failedCount++;
          } else {
            result.failedCount++;
            result.errors.push({
              id: item.id,
              error: 'Max retries exceeded'
            });
          }
        } catch (error) {
          console.error('Error executing request:', item.id, error);
          
          if (item.retryCount < item.maxRetries) {
            item.retryCount++;
            this.queue.push(item);
          }
          
          result.failedCount++;
          result.errors.push({
            id: item.id,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      // Clean up synced data
      await offlineStorage.clearSynced();

      result.success = result.failedCount === 0;
      this.notifyListeners(result.success ? 'success' : 'error', result);

      console.log('Sync completed:', result);
      return result;

    } catch (error) {
      console.error('Error processing sync queue:', error);
      result.success = false;
      this.notifyListeners('error', result);
      return result;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync offline data based on type
   */
  private async syncOfflineData(data: OfflineData): Promise<boolean> {
    console.log('Syncing offline data:', data.type, data.id);

    try {
      switch (data.type) {
        case 'website_update':
          return await this.syncWebsiteUpdate(data.data);
        
        case 'content_edit':
          return await this.syncContentEdit(data.data);
        
        case 'pet_listing':
          return await this.syncPetListing(data.data);
        
        case 'litter_update':
          return await this.syncLitterUpdate(data.data);
        
        case 'image_upload':
          return await this.syncImageUpload(data.data);
        
        default:
          console.warn('Unknown sync type:', data.type);
          return false;
      }
    } catch (error) {
      console.error('Error syncing data:', error);
      return false;
    }
  }

  /**
   * Sync website update
   */
  private async syncWebsiteUpdate(data: any): Promise<boolean> {
    const { id, updates } = data;
    
    const { error } = await supabase
      .from('breeder_websites')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Failed to sync website update:', error);
      return false;
    }

    return true;
  }

  /**
   * Sync content edit
   */
  private async syncContentEdit(data: any): Promise<boolean> {
    const { websiteId, updates } = data;
    
    const { error } = await supabase
      .from('breeder_websites')
      .update(updates)
      .eq('id', websiteId);

    if (error) {
      console.error('Failed to sync content edit:', error);
      return false;
    }

    return true;
  }

  /**
   * Sync pet listing
   */
  private async syncPetListing(data: any): Promise<boolean> {
    const { id, ...petData } = data;
    
    if (id) {
      // Update existing
      const { error } = await supabase
        .from('pet_listings')
        .update(petData)
        .eq('id', id);

      if (error) {
        console.error('Failed to sync pet listing update:', error);
        return false;
      }
    } else {
      // Insert new
      const { error } = await supabase
        .from('pet_listings')
        .insert(petData);

      if (error) {
        console.error('Failed to sync new pet listing:', error);
        return false;
      }
    }

    return true;
  }

  /**
   * Sync litter update
   */
  private async syncLitterUpdate(data: any): Promise<boolean> {
    const { id, ...litterData } = data;
    
    if (id) {
      const { error } = await supabase
        .from('litters')
        .update(litterData)
        .eq('id', id);

      if (error) {
        console.error('Failed to sync litter update:', error);
        return false;
      }
    } else {
      const { error } = await supabase
        .from('litters')
        .insert(litterData);

      if (error) {
        console.error('Failed to sync new litter:', error);
        return false;
      }
    }

    return true;
  }

  /**
   * Sync image upload
   */
  private async syncImageUpload(data: any): Promise<boolean> {
    const { file, bucket, path } = data;
    
    // Convert base64 back to file if needed
    let fileToUpload = file;
    if (typeof file === 'string' && file.startsWith('data:')) {
      const response = await fetch(file);
      const blob = await response.blob();
      fileToUpload = blob;
    }

    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, fileToUpload);

    if (error) {
      console.error('Failed to sync image upload:', error);
      return false;
    }

    return true;
  }

  /**
   * Execute a queued request with exponential backoff
   */
  private async executeRequest(item: SyncQueueItem): Promise<boolean> {
    const delay = this.calculateBackoffDelay(item.retryCount);
    
    if (item.retryCount > 0) {
      console.log(`Retrying request (attempt ${item.retryCount + 1}/${item.maxRetries}) after ${delay}ms`);
      await this.sleep(delay);
    }

    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: {
          'Content-Type': 'application/json',
          ...item.headers
        },
        body: JSON.stringify(item.body)
      });

      if (!response.ok) {
        console.error('Request failed:', response.status, response.statusText);
        return false;
      }

      console.log('Request succeeded:', item.id);
      return true;

    } catch (error) {
      console.error('Request error:', error);
      return false;
    }
  }

  /**
   * Calculate exponential backoff delay
   */
  private calculateBackoffDelay(retryCount: number): number {
    return Math.min(this.baseDelay * Math.pow(2, retryCount), 30000); // Max 30 seconds
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Retry failed items
   */
  async retryFailed(): Promise<SyncResult> {
    console.log('Retrying failed sync items...');
    return this.processQueue();
  }

  /**
   * Get current sync status
   */
  isSyncInProgress(): boolean {
    return this.isSyncing;
  }

  /**
   * Get queue size
   */
  getQueueSize(): number {
    return this.queue.length;
  }

  /**
   * Clear the queue (for testing)
   */
  clearQueue(): void {
    this.queue = [];
    console.log('Sync queue cleared');
  }
}

// Export singleton instance
export const backgroundSync = new BackgroundSyncManager();
